import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { AuthProvider, ProtectedRoute } from './contexts/AuthContext';
import { RoleProvider } from './contexts/RoleContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LandingPage from './pages/landing/LandingPage';
import LanguageSelectionModal from './components/LanguageSelectionModal';
import { ProgressProvider } from './contexts/ProgressContext';
import { UserDataProvider } from './contexts/UserDataContext';
import { AgentDataProvider } from './contexts/AgentDataContext';
import { AdminDataProvider } from './contexts/AdminDataContext';
import { ModeratorDataProvider } from './contexts/ModeratorDataContext';

// Security imports
import { applySecurityHeaders, checkSecurityContext } from './utils/securityHeaders';
import SessionManager from './components/SessionManager';
import RouteScrollToTop from './components/RouteScrollToTop';

const Login = lazy(() => import('./components/AuthPages/login'));
const SignUp = lazy(() => import('./components/AuthPages/SignUp'));
const ForgotPassword = lazy(() => import('./components/AuthPages/ForgotPassword'));
const ForgotPasswordCode = lazy(() => import('./components/AuthPages/ForgotPasswordCode'));
const ResetPassword = lazy(() => import('./components/AuthPages/ResetPassword'));
const VerifyCode = lazy(() => import('./components/AuthPages/VerifyCode'));

const UserApp = lazy(() => import('./pages/users/App'));
const AdminApp = lazy(() => import('./pages/admin/App'));
const ModeratorApp = lazy(() => import('./pages/moderator/App'));
const AgentApp = lazy(() => import('./pages/agents/App'));

function App() {
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  useEffect(() => {
    // Initialize security features
    checkSecurityContext();
    applySecurityHeaders();
    
    // Check language selection
    const languageSelected = localStorage.getItem('languageSelected');
    if (!languageSelected) {
      setShowLanguageModal(true);
    }
    
    // Add loaded class to root element for smooth transition
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.classList.add('loaded');
    }
  }, []);

  const handleCloseLanguageModal = () => {
    setShowLanguageModal(false);
  };

  return (
    <HelmetProvider>
      <ProgressProvider>
        <DarkModeProvider>
          <LayoutProvider>
            <AuthProvider>
              <RoleProvider>
            <ToastContainer 
              position="bottom-right" 
              autoClose={5000} 
              hideProgressBar={false} 
              className="z-[60]"
              toastClassName=""
              bodyClassName=""
              limit={2}
              style={{
                // On mobile, push up above the bottom navbar (which is ~96px tall)
                bottom: window.innerWidth < 640 ? '96px' : undefined,
                right: window.innerWidth < 640 ? '50%' : undefined,
                left: window.innerWidth < 640 ? '50%' : undefined,
                transform: window.innerWidth < 640 ? 'translate(-50%, 0)' : undefined,
                width: window.innerWidth < 640 ? 'calc(100vw - 2rem)' : undefined,
                maxWidth: window.innerWidth < 640 ? '400px' : undefined,
              }}
            />
            {showLanguageModal && (
              <LanguageSelectionModal onClose={handleCloseLanguageModal} />
            )}
            <SessionManager />
            <RouteScrollToTop />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/forgot-password-code" element={<ForgotPasswordCode />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-code" element={<VerifyCode />} />
                <Route
                  path="/user/*"
                  element={
                    <ProtectedRoute allowedRoles={['user']}>
                      <SubscriptionProvider>
                        <UserDataProvider>
                          <UserApp />
                        </UserDataProvider>
                      </SubscriptionProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminDataProvider>
                        <AdminApp />
                      </AdminDataProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/moderator/*"
                  element={
                    <ProtectedRoute allowedRoles={['moderator']}>
                      <ModeratorDataProvider>
                        <ModeratorApp />
                      </ModeratorDataProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agent/*"
                  element={
                    <ProtectedRoute allowedRoles={['agent']}>
                      <AgentDataProvider>
                        <AgentApp />
                      </AgentDataProvider>
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            </RoleProvider>
          </AuthProvider>
          </LayoutProvider>
        </DarkModeProvider>
      </ProgressProvider>
    </HelmetProvider>
  );
}

export default App;